import { Box, Flex, Grid, HStack, Spinner, Stack } from "@chakra-ui/react";
import { useEthers } from "@usedapp/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { AppContent } from "../../components/AppContent";
import { AppSelect, SelectItem } from "../../components/AppSelect";
import { I18N_NAMESPACE } from "../../core/constants/i18n";
import { ICollection } from "../../core/models/collection";
import { INft } from "../../core/models/nft";
import { collectionService } from "../../services/collection.service";
import { marketplaceService } from "../../services/marketplace.service";
import { NftItem } from "./components/NftItem";

const Marketplace = React.memo(() => {
  const { t } = useTranslation([I18N_NAMESPACE.MARKETPLACE]);

  const [collections, setCollections] = useState<ICollection[]>([]);
  const [nfts, setNfts] = useState<INft[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<SelectItem>();
  const [orderBy, setOrderBy] = useState<SelectItem>();
  const [isLoading, setIsLoading] = useState(false);
  const { account } = useEthers();

  const [page, setPage] = useState(1);
  const [isEndOfList, setIsEndOfList] = useState(false);
  const pageSize = 15;

  const collectionOptions = useMemo(
    () => collections.map((c) => ({ value: c.id, label: c.name || "" })),
    [collections]
  );
  const orderByOptions = useMemo(
    () => [
      { value: "none", label: t("None") },
      { value: "asc", label: t("Price: Low to High") },
      { value: "desc", label: t("Price: High to Low") },
    ],
    [t]
  );

  useEffect(() => {
    async function getCollections() {
      const response = await collectionService.getCollections();
      setCollections(response?.data || []);
    }

    getCollections();
  }, []);

  useEffect(() => {
    async function getNfts() {
      const collectionId = selectedCollection?.value.toString();
      const response = await marketplaceService.quickSearch({
        name: "",
        sort_direction: orderBy?.value !== "none" ? orderBy?.value?.toString() : undefined,
        sort_field: "price_mantissa",
        collection: collectionId,
        page: page,
        size: pageSize,
        account: account,
      });
      setIsLoading(false);
      setIsEndOfList(!response?.data || (response.data?.length || 0) < pageSize);

      if (page > 1) {
        setNfts((v) => [...v, ...(response.data || [])]);
      } else {
        setNfts(response?.data || []);
      }
    }

    getNfts();
  }, [orderBy, selectedCollection, page, account]);

  const onChangeCollection = useCallback((collection: SelectItem) => {
    setSelectedCollection(collection);
    setPage(1);
    setIsLoading(true);
  }, []);

  const onChangeOrderBy = useCallback((orderBy: SelectItem) => {
    setOrderBy(orderBy);
    setPage(1);
    setIsLoading(true);
  }, []);

  return (
    <AppContent>
      <Stack
        direction={{ base: "column", md: "row" }}
        justifyContent={{ base: "space-between", md: "flex-end" }}
        align={{ base: "stretch", md: "flex-end" }}
        gap="2">
        <Box minW="200px">
          <AppSelect
            options={[{ value: "", label: t("All") }, ...collectionOptions]}
            value={selectedCollection}
            placeholder={t("Collections")}
            onChange={onChangeCollection}
          />
        </Box>
        <Box minW="200px">
          <AppSelect options={orderByOptions} value={orderBy} placeholder={t("Sort By")} onChange={onChangeOrderBy} />
        </Box>
      </Stack>
      {isLoading ? (
        <Flex alignItems="center" justifyContent="center" marginTop={6}>
          <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
        </Flex>
      ) : (
        <InfiniteScroll
          style={{
            overflow: "hidden",
          }}
          dataLength={nfts.length}
          next={() => {
            setPage((p) => p + 1);
          }}
          hasMore={!isEndOfList}
          loader={
            <Flex alignItems="center" justifyContent="center" marginTop={10}>
              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
            </Flex>
          }
        >
          <Grid
            templateColumns={{
              base: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
              xl: "repeat(5, 1fr)",
              "2xl": "repeat(6, 1fr)",
            }}
            gap={6}
            marginTop={6}>
            {nfts.map((nft) => (
              <NftItem nft={nft} account={account} key={`${nft.token_address}-${nft.token_id}-${nft.owner_of}`} />
            ))}
          </Grid>
        </InfiniteScroll>
      )}
    </AppContent>
  );
});

export default Marketplace;
